import React from 'react';
import PropTypes from 'prop-types';

import {
  buildDateFormatter,
  buildSafeDateFormatter,
  isChampvaProviderClaim,
} from '~/platform/shared/claims-appeals';
import { isClaimOpen } from '../utils/helpers';
import * as TrackedItem from '../utils/trackedItemContent';

// Hoisted to module scope rather than rebuilt per render, matching ClaimContent
// and AppealContent in platform/shared/claims-appeals/components.
const formatDate = buildDateFormatter();
const formatDateSafely = buildSafeDateFormatter();

export default function ClaimStatusHeader({ claim }) {
  const {
    closeDate,
    status,
    trackedItems,
    claimPhaseDates,
    claimStatusMeta,
    latestUpdateDate,
  } = claim.attributes;
  const headerLabel = claimStatusMeta?.statusHeader?.label || 'Claim status';
  const introText =
    claimStatusMeta?.statusHeader?.intro ||
    'Here’s the latest information on your claim.';
  const getTrackedItemDates = () => {
    return trackedItems
      ? trackedItems.map(item => TrackedItem.getTrackedItemDateFromStatus(item))
      : [];
  };
  // CHAMPVA claims get their "Last updated" date from the backend, which returns
  // the most recent VES eligibility determination or qualifying CCL letter date
  // across every applicant on the application (story #152133; the derivation is
  // BenefitsClaims::Providers::IvcChampva::LatestUpdateDate). No date comparison
  // happens here on purpose — the backend owns that selection, so the two can
  // never drift. Every other claim type keeps computing its own date below.
  //
  // Returns null when there is nothing to show, which drops the whole line rather
  // than leaving a bare "Last updated:" label behind. Two ways to get there:
  // the serializer omits latestUpdateDate entirely (rather than sending null)
  // when no qualifying activity exists, and buildSafeDateFormatter returns null
  // for anything unparseable. buildDateFormatter would render the literal string
  // 'Invalid date' in that second case, which a Veteran would see.
  //
  // For CHAMPVA, an absent date is the normal state between submission and the
  // first determination — not only a VES failure.
  const getFormattedLastUpdated = () => {
    if (isChampvaProviderClaim(claim)) {
      return formatDateSafely(latestUpdateDate);
    }

    const phaseChangeDate = claimPhaseDates
      ? claimPhaseDates.phaseChangeDate
      : null;
    const dates = [...getTrackedItemDates(), phaseChangeDate];
    const lastUpdatedDate = dates.reduce((a, b) => (a > b ? a : b));

    return formatDate(lastUpdatedDate);
  };
  const formattedLastUpdated = getFormattedLastUpdated();
  const isOpen = isClaimOpen(status, closeDate);

  return (
    <div className="claim-status-header-container">
      <h2 className="tab-header vads-u-margin-y--0">{headerLabel}</h2>
      <p className="vads-u-margin-top--1 vads-u-margin-bottom--3 va-introtext">
        {claimStatusMeta?.statusHeader?.intro
          ? introText
          : 'Review the latest status of your claim.'}
      </p>
      <div className="vads-u-margin-bottom--4">
        <span className="usa-label">{isOpen ? 'In Progress' : 'Closed'}</span>
        {formattedLastUpdated && (
          <p className="vads-u-margin-top--1 vads-u-margin-bottom--0">
            {`Last updated: ${formattedLastUpdated}`}
          </p>
        )}
      </div>
    </div>
  );
}

ClaimStatusHeader.propTypes = {
  claim: PropTypes.object,
};
